/**
 * Makes the content source injectable (tasks 9.5, 9.6, 9.7).
 *
 * Screens ask the context rather than reaching for `fetch`, so a test hands
 * them a package without a server and the delivery layer can wrap the real
 * source in caching and digest verification without touching a screen.
 */
import { createContext, useContext, useMemo, type JSX, type ReactNode } from 'react';
import { deliveringContentSource, type ContentSource } from './content/delivery.js';

const ContentContext = createContext<ContentSource | null>(null);

export function ContentProvider({
  source,
  children,
}: {
  readonly source?: ContentSource | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  const value = useMemo(() => source ?? deliveringContentSource(), [source]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContentSource(): ContentSource {
  const source = useContext(ContentContext);
  if (!source) throw new Error('useContentSource must be used inside a ContentProvider.');
  return source;
}
